import {
  useCallback,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';

import useDebounce from 'hooks/useDebounce';
import { Option } from 'lib/models/option';
import { TagsResponse, TagAttributes, TagType } from 'lib/models/tag';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { parseArrayResponse, parsePaginatedResponse } from 'lib/utils/parser';

import {
  BasicStudentSocietiesResponse,
  BasicStudentSocietyAttributes,
  PER_PAGE_COUNT,
} from 'lib/models/basic-student-society';

export interface UniversityOption extends Option {
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

interface ReturnType {
  universities: Array<UniversityOption>;
  universityMap: { [slug: string]: UniversityOption };
  topics: Array<Option>;
  topicSlugs: Array<string>;
  setTopicSlugs: Dispatch<SetStateAction<string[]>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  size: number;
  setSize: (size: number) => void;
  societies: Array<BasicStudentSocietyAttributes>;
  resetFilter: () => void;
  isFilterApplied: boolean;
  isLoading: boolean;
  hasMoreRecords: boolean;
  totalRecords: number;
}

const createFilterParams = ({
  universitySlug,
  topicSlugs,
  page,
  name,
}: {
  universitySlug: string;
  topicSlugs: Array<string>;
  page: number;
  name: string;
}) => {
  const params = {
    basic_details: true,
    with_icon: true,
    university_ids: [universitySlug],
    topic_ids: topicSlugs,
    page,
    name,
    per_page: PER_PAGE_COUNT,
  };
  return queryString.stringify(params, {
    arrayFormat: 'bracket',
    skipEmptyString: true,
  });
};

export const getInitialFilterParams = (universitySlug: string): string => {
  return createFilterParams({
    universitySlug: universitySlug,
    topicSlugs: [],
    name: '',
    page: 1,
  });
};

export const useBasicStudentSocieties = ({
  universitySlug,
  initialSocieties,
}: {
  universitySlug: string;
  initialSocieties: BasicStudentSocietiesResponse;
}): ReturnType => {
  const [topicSlugs, setTopicSlugs] = useState<Array<string>>([]);
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 1000) as string;
  const universitySlugRef = useRef(universitySlug);
  const sizeRef = useRef(0);

  const resetFilter = useCallback(() => {
    setTopicSlugs([]);
    setSearch('');
  }, []);

  const getSocietiesKey = (pageIndex: number) => {
    sizeRef.current = pageIndex;
    const params = createFilterParams({
      universitySlug,
      topicSlugs,
      name: debouncedSearch,
      page: pageIndex + 1,
    });
    return `${API_ROUTES.STUDENT_SOCIETIES}?${params}`;
  };

  const initialFilter = getInitialFilterParams(universitySlugRef.current);
  const initialPathname = `${API_ROUTES.STUDENT_SOCIETIES}?${initialFilter}`;

  // feature of useSWRInfinite to make first page call everytime new page is fetched
  // https://github.com/vercel/swr/issues/1401
  const {
    data: studentSocietiesResponse,
    error: studentSocietiesError,
    size,
    setSize,
  } = useSWRInfinite<BasicStudentSocietiesResponse>(getSocietiesKey, get, {
    fallbackData:
      initialPathname === getSocietiesKey(sizeRef.current)
        ? [initialSocieties]
        : undefined,
    persistSize: false,
    revalidateFirstPage: false,
  });

  const { data: universitiesResponse } = useSWR<BasicStudentSocietiesResponse>(
    API_ROUTES.UNIVERSITIES,
    get
  );

  const { data: topicsResponse } = useSWR<TagsResponse<TagType.Topic>>(
    API_ROUTES.TOPICS,
    get
  );

  const isLoading = !studentSocietiesResponse && !studentSocietiesError;

  const societies = useMemo(() => {
    return parsePaginatedResponse<BasicStudentSocietyAttributes>(
      studentSocietiesResponse
    );
  }, [studentSocietiesResponse]);

  const totalRecords = useMemo(() => {
    const firstSocietyList = Array.isArray(studentSocietiesResponse)
      ? studentSocietiesResponse[0]
      : null;
    return firstSocietyList ? firstSocietyList.meta.total : 0;
  }, [studentSocietiesResponse]);

  const hasMoreRecords = useMemo(() => {
    const firstSocietyList = Array.isArray(studentSocietiesResponse)
      ? studentSocietiesResponse[0]
      : null;
    if (firstSocietyList) {
      return firstSocietyList.meta.total > size * PER_PAGE_COUNT;
    } else {
      return false;
    }
  }, [studentSocietiesResponse, size]);

  const { list: universities, map: universityMap } = useMemo(() => {
    const universities =
      parseArrayResponse<BasicStudentSocietyAttributes>(universitiesResponse);
    return universities.reduce(
      function (
        {
          list,
          map,
        }: {
          list: Array<UniversityOption>;
          map: { [slug: string]: UniversityOption };
        },
        university
      ) {
        const {
          id,
          slug,
          name: label,
          icon_url: avatar,
          meta_title,
          meta_description,
          meta_tags,
        } = university;
        const tags = parseArrayResponse(university.meta_tags);
        const metaKeywords = tags.map((tag) => tag.value).join(', ');

        const option = {
          id,
          value: slug,
          label,
          avatar,
          metaTitle: meta_title,
          metaDescription: meta_description,
          metaKeywords,
        };

        list.push(option);
        map[slug] = option;

        return { list, map };
      },
      { list: [], map: {} }
    );
  }, [universitiesResponse]);

  const topics = useMemo(() => {
    const list =
      parseArrayResponse<TagAttributes<TagType.Topic>>(topicsResponse);
    return list.map(({ id, slug, name }) => ({
      id: id,
      value: slug,
      label: name,
    }));
  }, [topicsResponse]);

  return {
    universities,
    universityMap,
    topics,
    topicSlugs,
    setTopicSlugs,
    search,
    setSearch,
    size,
    setSize,
    societies,
    resetFilter,
    isFilterApplied: topicSlugs.length > 0,
    isLoading,
    hasMoreRecords,
    totalRecords,
  };
};
